import axios from 'axios'
import { fileServices } from '../../../services/fileServices'
import {
  CURRENCY_COUNTRY_CODE,
  FIELD_TYPES,
  HTTP_METHOD,
  OUTBOUND_SERVER_ACTION_LIST_DATA_TYPE,
} from '../../../utility/constants'
import { processDataService } from '../../../services/processDataService'
import { userConfig } from '../../../services/userConfigServices'

const getListDataValue = (
  listDataId,
  listDataMetadata,
  listDataType,
  listDataMetadataKey,
  isDynamicList = false
) => {
  if (isDynamicList) {
    if (listDataType === OUTBOUND_SERVER_ACTION_LIST_DATA_TYPE.METADATA) {
      return listDataMetadata.find((metadata) => metadata.id === listDataId)?.metadata[
        listDataMetadataKey
      ]
    } else if (listDataType === OUTBOUND_SERVER_ACTION_LIST_DATA_TYPE.ID) {
      return listDataId
    } else {
      return listDataMetadata.find((metadata) => metadata.id === listDataId)?.value
    }
  } else {
    if (listDataType === OUTBOUND_SERVER_ACTION_LIST_DATA_TYPE.METADATA) {
      return listDataMetadata.find((metadata) => metadata.list_data_id === listDataId)
        ?.list_data_metadata[listDataMetadataKey]
    } else if (listDataType === OUTBOUND_SERVER_ACTION_LIST_DATA_TYPE.ID) {
      return listDataId
    } else {
      return listDataMetadata.find((metadata) => metadata.list_data_id === listDataId)
        ?.list_data_value
    }
  }
}

export const replacePlaceholders = (txt, data) => {
  return txt?.replace(/\[([^\]]+)\]/g, (_, key) => data[key] || '')
}

const getNestedValue = (obj, path) => {
  return path
    .split('.')
    .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : null), obj)
}

const getActualValue = async (
  field,
  fieldId,
  type = null,
  subType = null,
  allValues,
  dynamicList,
  listDataMetadata,
  user,
  fieldCurrencyMap,
  defaultCurrency
) => {
  let returnValue = null

  if (field.data_structure === FIELD_TYPES.FILE) {
    /*

   type :{
   base64: return base64 string,
   default : return file s3 uuid
   }

   */
    const value =
      type === 'base64'
        ? await fileServices.getBase64(user, allValues['f_' + fieldId])
        : allValues['f_' + fieldId]
    returnValue = value
  } else if (field.data_structure === FIELD_TYPES.LIST) {
    /*

   type :{
   id: return list_data_id,
   metadata: return list_data metadata[subType]
   default : return actual value of list data
   }
   
   */
    const dynamicFieldType = field?.dynamic_field_type?.type
    if (dynamicFieldType === 'users') {
      const value = getListDataValue(
        allValues['f_' + fieldId],
        dynamicList.usersList,
        type,
        subType,
        true
      )
      returnValue = value
    } else if (dynamicFieldType === 'teams') {
      const value = getListDataValue(
        allValues['f_' + fieldId],
        dynamicList.teamsList,
        type,
        subType,
        true
      )
      returnValue = value
    } else {
      const value = getListDataValue(allValues['f_' + fieldId], listDataMetadata, type, subType)
      returnValue = value
    }
  } else if (field.data_structure === FIELD_TYPES.LIST_MULTI) {
    const dynamicFieldType = field?.dynamic_field_type?.type
    const selectedListDataIds = allValues['f_' + fieldId] || []

    const finalValue = []

    for (let listDataId of selectedListDataIds) {
      if (dynamicFieldType === 'users') {
        const value = getListDataValue(listDataId, dynamicList.usersList, type, subType, true)
        finalValue.push(value)
      } else if (dynamicFieldType === 'teams') {
        const value = getListDataValue(listDataId, dynamicList.teamsList, type, subType, true)
        finalValue.push(value)
      } else {
        const value = getListDataValue(listDataId, listDataMetadata, type, subType)
        finalValue.push(value)
      }
    }
    returnValue = finalValue.join(',')
  } else if (field.data_structure === FIELD_TYPES.CURRENCY) {
    /*

   type :{
   numeric: return value,
   default : return value with currency code , eg: USD:1000
   }
   
   */

    const value =
      type === 'numeric'
        ? allValues['f_' + fieldId]
        : `${getCurrencyCode(fieldId, fieldCurrencyMap, defaultCurrency)}:${
            allValues['f_' + fieldId]
          }`
    returnValue = value
  } else {
    const value = allValues['f_' + fieldId] ?? null
    returnValue = value
  }

  return returnValue
}

const getCurrencyCode = (fieldId, fieldCurrencyMap, defaultCurrency) => {
  const fieldTemplateId = fieldId.toString().split('__', 1)[0]
  const countryCode = fieldCurrencyMap[fieldTemplateId]

  if (countryCode) {
    for (const [currencyCode, code] of Object.entries(CURRENCY_COUNTRY_CODE)) {
      if (code === countryCode) return currencyCode
    }
  }

  return defaultCurrency
}

export const executeOutboundServerAction = async (
  user,
  processId,
  activityTemplateId,
  selectedToRoute,
  allValues,
  fields,
  listDataMetadata,
  tableSubfieldsMap = [],
  tableSequences = [],
  reservedKeywords = {},
  fieldCurrencyMap = {},
  defaultCurrency
) => {
  const actions = (
    await processDataService.outboundServerActions(user, activityTemplateId, selectedToRoute)
  )?.data

  let finalApiResponses = []
  allValues = { ...allValues, ...reservedKeywords }

  const dynamicList = (await userConfig.getDynamicList(user)).data

  const translatePayload = async (schema, allValues = {}, previousResponse = {}) => {
    // object
    if (schema.type === 'object') {
      /*
          schema ={
          type:'object',
          'properties':{
          'key1':{...}
          }
          }
          */
      const properties = schema.properties
      const result = {}
      if (properties) {
        for (let key in properties) {
          result[key] = await translatePayload(properties[key], allValues, previousResponse)
        }
      }
      return result
    }
    // string || number
    else if (schema.type === 'string' || schema.type === 'number') {
      /*
          schema ={
          type:'string'|'number',
          'value':'[[field_id?.type?.subType]]'|'basic [[x_response1.Response.AuthToken]]'
                  |'[[x_process_reference_num]]'|'[[x_prefix]]-[[x_process_reference_num]]'
          }
          */
      let value = schema?.value

      const matches = value?.match(/\[\[(.*?)\]\]/g) || []

      if (matches?.length > 0) {
        for (let match of matches) {
          let replaceValue = schema.type === 'number' ? 0 : ''
          const key = match.slice(2, -2)

          // data from previous responses
          /*
          (eg 1: x_response1.Response.AuthToken)
          (eg 2: x_response2.data.email)
          */

          if (key?.startsWith('x_response')) {
            const path = key.substr(2)
            replaceValue = getNestedValue(previousResponse, path)
          }

          // data from reserved keys
          /*
          (x_[reserved_key])
          (eg 1: x_process_reference_num)
          (eg 2: x_prefix)
          */
          else if (key?.startsWith('x_') && !key?.startsWith('x_response')) {
            const reservedKey = key.substr(2)
            replaceValue = allValues[reservedKey]
          }
          // data from fields
          else {
            const [fieldId, type, subType] = key?.split('.')
            const fieldIdWithoutRow = fieldId?.includes('__') ? fieldId.split('__')[0] : fieldId
            const field = fields?.find((field) => field.id == fieldIdWithoutRow)
            if (field) {
              replaceValue = await getActualValue(
                field,
                fieldId,
                type,
                subType,
                allValues,
                dynamicList,
                listDataMetadata,
                user,
                fieldCurrencyMap,
                defaultCurrency
              )
            }
          }

          value = value?.replace(match, replaceValue)
        }
      }

      if (schema.type === 'number' && !isNaN(value)) {
        value = Number(value)
      }
      return value
    }
    // array
    else if (schema.type === 'array') {
      /*
          schema ={
          type:'array',
         'items':{...}
          */

      const items = schema.items
      const result = []
      if (items) {
        // array of objects
        if (items.type === 'object') {
          const properties = items.properties
          let firstSubfieldKey = null

          for (let key in properties) {
            let value = { ...properties[key] }
            firstSubfieldKey = value?.value?.match(/\[\[(.*?)\]\]/g)?.reduce((acc, it) => {
              const data = it.slice(2, -2)

              const id = data?.split('.')[0]

              if (!isNaN(id)) {
                const isSubfield = fields.find((it) => it.id == id)?.is_subfield
                if (isSubfield) {
                  acc = id
                }
              }

              return acc
            }, 0)

            if (firstSubfieldKey) {
              break
            }
          }

          if (firstSubfieldKey) {
            if (properties) {
              const tableId = tableSubfieldsMap.find(
                (it) => it.subfield_template_id == firstSubfieldKey
              )?.table_field_template_id
              const sequence = tableSequences.find(
                (it) => it.table_field_template_id == tableId
              )?.sequence_header_map

              if (sequence) {
                for (const [index, it] of sequence.entries()) {
                  const row = it.row
                  const result1 = {}
                  for (let key in properties) {
                    let value = { ...properties[key] }

                    const subfieldValue = value?.value
                      ?.match(/\[\[(.*?)\]\]/g)
                      ?.reduce((acc, it) => {
                        const data = it.slice(2, -2)

                        const [id, ...rest] = data?.split('.')

                        if (!isNaN(id)) {
                          const isSubfield = fields.find((it) => it.id == id)?.is_subfield
                          if (isSubfield) {
                            acc = value?.value?.replace(
                              it,
                              `[[${[id + '__' + row, ...rest].join('.')}]]`
                            )
                          }
                        }

                        return acc
                      }, 0)
                    if (subfieldValue) {
                      value.value = subfieldValue
                    } else if (value.value === '$slno') {
                      value.value = index + 1 + ''
                    }
                    result1[key] = await translatePayload(value, allValues, previousResponse)
                  }
                  result.push(result1)
                }
              }
            }
          } else {
            if (properties) {
              const result1 = {}
              for (let key in properties) {
                result1[key] = await translatePayload(properties[key], allValues, previousResponse)
              }
              result.push(result1)
            }
          }
        } else {
          let values = items.value
          // default array values
          if (Array.isArray(values)) {
            for (let value of values) {
              result.push(value)
            }
          } else {
            const itemsValue = items?.value?.slice(2, -2)
            // array of field values
            if (itemsValue && itemsValue?.length > 0) {
              const [fieldId, type, subType] = itemsValue?.split('.')
              const field = fields.find((it) => it.id == fieldId)
              if (field) {
                //   if subfields
                if (field.is_subfield) {
                  const tableId = tableSubfieldsMap.find(
                    (it) => it.subfield_template_id == fieldId
                  )?.table_field_template_id
                  const sequence = tableSequences.find(
                    (it) => it.table_field_template_id == tableId
                  )?.sequence_header_map
                  if (sequence) {
                    for (let it of sequence) {
                      const fieldIdWithRow = fieldId + '__' + it.row
                      const value = await getActualValue(
                        field,
                        fieldIdWithRow,
                        type,
                        subType,
                        allValues,
                        dynamicList,
                        listDataMetadata,
                        user,
                        fieldCurrencyMap,
                        defaultCurrency
                      )
                      result.push(value)
                    }
                  }
                } else {
                  //   if normal field
                  if (field.data_structure === FIELD_TYPES.LIST_MULTI) {
                    const values = (
                      await getActualValue(
                        field,
                        field.id,
                        type,
                        subType,
                        allValues,
                        dynamicList,
                        listDataMetadata,
                        user,
                        fieldCurrencyMap,
                        defaultCurrency
                      )
                    )?.split(',')

                    if (Array.isArray(values)) {
                      for (let value of values) {
                        result.push(value)
                      }
                    }
                  } else {
                    const value = await getActualValue(
                      field,
                      field.id,
                      type,
                      subType,
                      allValues,
                      dynamicList,
                      listDataMetadata,
                      user,
                      fieldCurrencyMap,
                      defaultCurrency
                    )
                    result.push(value)
                  }
                }
              }
            }
          }
        }
      }
      return result
    }
    // const values
    else if (schema.hasOwnProperty('const')) {
      /*
          schema ={
          const:'const_value',
          }
          */
      return schema.const
    } else {
      const itemsValue = schema?.value?.slice(2, -2)
      const field = fields.find((it) => it.id == itemsValue?.split('.')[0])

      // custom view field
      if (field && field?.data_structure === FIELD_TYPES.CUSTOM_VIEW) {
        const [fieldId, type, subType] = itemsValue?.split('.')
        const values = allValues['f_' + fieldId]
        if (values && type && subType) {
          return values[type][subType]
        } else if (values && type) {
          return values[type]
        } else {
          return values
        }
      } else {
        return schema
      }
    }
  }

  let previousResponse = {}

  for (const actionItem of actions) {
    let payload = {}
    let headers = {}
    let url = ''

    if (actionItem.custom_payload_schema) {
      payload = await translatePayload(
        actionItem.custom_payload_schema,
        allValues,
        previousResponse
      )
    }

    if (actionItem.headers) {
      headers = await translatePayload(actionItem.headers, allValues, previousResponse)
    }

    const method = actionItem.http_request_method

    let axiosOptions = { headers }
    try {
      if (method == HTTP_METHOD.GET) {
        url = actionItem.endpoint
      }

      if ([HTTP_METHOD.POST, HTTP_METHOD.PUT].includes(method)) {
        url = replacePlaceholders(actionItem.endpoint, { ...payload, ...reservedKeywords })
      }

      const apiResponse = await sendHttpRequest(url, method, payload, axiosOptions)

      previousResponse['response' + actionItem.sequence] = apiResponse.data
      finalApiResponses.push({
        outbound_server_action_id: actionItem.id,
        status: true,
        http_status_code: apiResponse?.status,
        message: 'successful',
      })
    } catch (err) {
      console.error(err)
      finalApiResponses.push({
        outbound_server_action_id: actionItem.id,
        status: false,
        http_status_code: err?.response?.status,
        message: err?.message,
      })
    }
  }

  // update the individual api call status
  await processDataService.updateOutboundServerActions(user, finalApiResponses, processId)
}

export const sendHttpRequest = async (url, method, payload, axiosOptions) => {
  let result
  if (method === HTTP_METHOD.GET) {
    result = await axios.get(url, {
      params: payload,
      ...axiosOptions,
    })
  } else if (method === HTTP_METHOD.POST) {
    result = await axios.post(url, payload, axiosOptions)
  } else if (method === HTTP_METHOD.PUT) {
    result = await axios.put(url, payload, axiosOptions)
  }
  return result
}
